import type {
  CourseMetadataTranslationPatch,
  CourseMetadataTranslationProjection,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { COURSE_METADATA_TRANSLATED_FIELDS } from '@course-authoring/shared-types';
import { useScopedMetadataAutosave } from '../../../hooks/useScopedMetadataAutosave';

export interface CourseMetadataDraft {
  title: string;
  description: string;
  certificateMessage: string;
  completedMessage: string;
}

export function courseMetadataDraftFromProjection(
  projection: CourseMetadataTranslationProjection,
): CourseMetadataDraft {
  return {
    title: projection.title,
    description: projection.description,
    certificateMessage: projection.certificateMessage,
    completedMessage: projection.completedMessage,
  };
}

export function buildCourseMetadataPatch(
  baseline: CourseMetadataDraft,
  current: CourseMetadataDraft,
): CourseMetadataTranslationPatch {
  const patch: CourseMetadataTranslationPatch = {};
  if (current.title !== baseline.title) patch.title = current.title;
  if (current.description !== baseline.description) patch.description = current.description;
  if (current.certificateMessage !== baseline.certificateMessage) {
    patch.certificateMessage = current.certificateMessage;
  }
  if (current.completedMessage !== baseline.completedMessage) {
    patch.completedMessage = current.completedMessage;
  }
  return patch;
}

export function useCourseMetadataAutosave(options: {
  enabled: boolean;
  scopeKey: string;
  initialProjection: CourseMetadataTranslationProjection | undefined;
  replacementAction?: TranslationReplacementAction;
  save: (patch: CourseMetadataTranslationPatch) => Promise<CourseMetadataTranslationProjection>;
  onSaved?: (
    projection: CourseMetadataTranslationProjection,
    patch: CourseMetadataTranslationPatch,
  ) => void;
  intervalMs?: number;
}) {
  return useScopedMetadataAutosave({
    ...options,
    projectionRevisionKey: [
      options.initialProjection?.title,
      options.initialProjection?.description,
      options.initialProjection?.certificateMessage,
      options.initialProjection?.completedMessage,
    ].join('\u0000'),
    emptyDraft: {
      title: '',
      description: '',
      certificateMessage: '',
      completedMessage: '',
    },
    draftFromProjection: courseMetadataDraftFromProjection,
    buildPatch: buildCourseMetadataPatch,
    hasSourceChanges: (patch) => (
      COURSE_METADATA_TRANSLATED_FIELDS.some((field) => field in patch)
    ),
    mergeNewerDraft,
    buildDecisionPatch: (baseline, current, action) => ({
      ...buildCourseMetadataPatch(baseline, current),
      replacementAction: action,
    }),
  });
}

function mergeNewerDraft(
  server: CourseMetadataDraft,
  submitted: CourseMetadataDraft,
  current: CourseMetadataDraft,
): CourseMetadataDraft {
  return {
    title: current.title !== submitted.title ? current.title : server.title,
    description: current.description !== submitted.description ? current.description : server.description,
    certificateMessage: current.certificateMessage !== submitted.certificateMessage
      ? current.certificateMessage
      : server.certificateMessage,
    completedMessage: current.completedMessage !== submitted.completedMessage
      ? current.completedMessage
      : server.completedMessage,
  };
}
